import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import type { User } from '../types';
import { adminApi } from '../services/api';

const AdminPage: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loadingUsers, setLoadingUsers] = useState(false);

  // Create user form state
  const [newEmail, setNewEmail] = useState('');
  const [newIsAdmin, setNewIsAdmin] = useState(false);
  const [newIsActive, setNewIsActive] = useState(true);
  const [createUserResetLink, setCreateUserResetLink] = useState<string | null>(null);
  const [creatingUser, setCreatingUser] = useState(false);

  // Per-user reset link map
  const [resetLinks, setResetLinks] = useState<Record<number, string>>({});

  const fetchUsers = async () => {
    setLoadingUsers(true);
    setError(null);
    try {
      const userList = await adminApi.getUsers();
      setUsers(userList);
    } catch (err) {
      setError('Failed to fetch users.');
    } finally {
      setLoadingUsers(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const origin = useMemo(() => (typeof window !== 'undefined' ? window.location.origin : ''), []);

  const handleApprove = async (userId: number) => {
    setError(null);
    try {
      await adminApi.approveUser(userId);
      await fetchUsers();
    } catch (err) {
      setError('Failed to approve user.');
    }
  };

  const handleReject = async (userId: number) => {
    setError(null);
    try {
      await adminApi.rejectUser(userId);
      await fetchUsers();
    } catch (err) {
      setError('Failed to reject user.');
    }
  };

  const handleDisable = async (userId: number) => {
    setError(null);
    try {
      await adminApi.disableUser(userId);
      await fetchUsers();
    } catch (err) {
      setError('Failed to disable user.');
    }
  };

  const handleEnable = async (userId: number) => {
    setError(null);
    try {
      await adminApi.enableUser(userId);
      await fetchUsers();
    } catch (err) {
      setError('Failed to enable user.');
    }
  };

  const handleGenerateResetLink = async (userId: number) => {
    setError(null);
    try {
      const { reset_token } = await adminApi.generateResetToken(userId);
      const link = `${origin}/reset?token=${encodeURIComponent(reset_token)}`;
      setResetLinks((prev) => ({ ...prev, [userId]: link }));
    } catch (err) {
      setError('Failed to generate reset link.');
    }
  };

  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreatingUser(true);
    setError(null);
    setCreateUserResetLink(null);
    try {
      const { user, reset_token } = await adminApi.createUser({
        email: newEmail,
        is_admin: newIsAdmin,
        is_active: newIsActive,
      });
      const link = `${origin}/reset?token=${encodeURIComponent(reset_token)}`;
      setCreateUserResetLink(link);
      setNewEmail('');
      setNewIsAdmin(false);
      setNewIsActive(true);
      await fetchUsers();
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Failed to create user.');
    } finally {
      setCreatingUser(false);
    }
  };

  const renderStatus = (u: User) => {
    if (!u.is_approved) return 'Pending Approval';
    if (u.is_approved && !u.is_active) return 'Disabled';
    return 'Active';
  };

  return (
    <div className="space-y-8">
      <Card>
        <CardHeader>
          <CardTitle>Create User</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleCreateUser} className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
            <div className="space-y-2">
              <Label htmlFor="new-email">Email</Label>
              <Input
                id="new-email"
                type="email"
                placeholder="user@example.com"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                required
              />
            </div>
            <div className="space-y-2">
              <Label className="block">Role</Label>
              <div className="flex items-center space-x-3">
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={newIsAdmin}
                    onChange={(e) => setNewIsAdmin(e.target.checked)}
                  />
                  <span>Admin</span>
                </label>
              </div>
            </div>
            <div className="space-y-2">
              <Label className="block">Active</Label>
              <div className="flex items-center space-x-3">
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={newIsActive}
                    onChange={(e) => setNewIsActive(e.target.checked)}
                  />
                  <span>Active (can log in)</span>
                </label>
              </div>
            </div>
            <div>
              <Button type="submit" disabled={creatingUser || !newEmail}>
                {creatingUser ? 'Creating...' : 'Create User'}
              </Button>
            </div>
          </form>
          {createUserResetLink && (
            <div className="mt-4 p-3 rounded border text-sm">
              <div className="font-medium mb-1">Initial password setup link</div>
              <div className="break-all">{createUserResetLink}</div>
              <div className="mt-2">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => navigator.clipboard.writeText(createUserResetLink)}
                >
                  Copy link
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>User Management</CardTitle>
        </CardHeader>
        <CardContent>
          {error && <p className="text-red-500 mb-3">{error}</p>}
          <div className="border rounded-lg w-full overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ID</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Role</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loadingUsers ? (
                  <tr>
                    <td colSpan={5} className="px-6 py-4 text-sm text-gray-500">
                      Loading users...
                    </td>
                  </tr>
                ) : users.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-6 py-4 text-sm text-gray-500">
                      No users found.
                    </td>
                  </tr>
                ) : (
                  users.map((user) => (
                    <tr key={user.id}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{user.id}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{user.email}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{renderStatus(user)}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{user.is_admin ? 'Admin' : 'User'}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                        {!user.is_approved && (
                          <>
                            <Button onClick={() => handleApprove(user.id)} size="sm">
                              Approve
                            </Button>
                            <Button onClick={() => handleReject(user.id)} size="sm" variant="destructive">
                              Reject
                            </Button>
                          </>
                        )}
                        {user.is_approved && user.is_active && (
                          <>
                            <Button onClick={() => handleDisable(user.id)} size="sm" variant="secondary">
                              Disable
                            </Button>
                            <Button onClick={() => handleGenerateResetLink(user.id)} size="sm">
                              Generate reset link
                            </Button>
                          </>
                        )}
                        {user.is_approved && !user.is_active && (
                          <>
                            <Button onClick={() => handleEnable(user.id)} size="sm">
                              Enable
                            </Button>
                            <Button onClick={() => handleGenerateResetLink(user.id)} size="sm" variant="secondary">
                              Generate reset link
                            </Button>
                          </>
                        )}
                        {resetLinks[user.id] && (
                          <div className="mt-2 text-xs text-gray-600 break-all">
                            {resetLinks[user.id]}
                            <div className="mt-1">
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => navigator.clipboard.writeText(resetLinks[user.id])}
                              >
                                Copy
                              </Button>
                            </div>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminPage;
